import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BrandLogo } from "@/components/BrandLogo";
import { firstAllowedAdminPath } from "@/constants/adminMenu";
import { Loader2, ShieldCheck, UserCog, Users, Wrench } from "lucide-react";

const DEMO_ACCOUNTS = [
    { email: "admin@trulynolen.com.sv", password: "trulyn123", role: "Administrador", icon: ShieldCheck },
    { email: "operador@trulynolen.com.sv", password: "trulyn123", role: "Operador", icon: ShieldCheck },
    { email: "tecnico@trulynolen.com.sv", password: "trulyn123", role: "Técnico", icon: Wrench },
    { email: "cliente@grupocarsen.sv", password: "trulyn123", role: "Cliente", icon: Users },
    { email: "admin@pollocampero.sv", password: "trulyn123", role: "Cliente (Sucursales)", icon: Users },
];

export default function LoginPage() {
    const { login } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Ingrese correo y contraseña");
            return;
        }
        setLoading(true);
        try {
            // Always start from a clean session: remove any previous token/user/cache
            try { localStorage.removeItem("trulyn_token"); localStorage.removeItem("trulyn_user"); } catch {}
            const u = await login(email.trim(), password);
            toast.success(`Bienvenido, ${u.full_name}`);
            const home = u.role === "technician" ? "/tech" : u.role === "client" ? "/portal" : u.role === "auditor" ? "/audit" : firstAllowedAdminPath(u);
            // Always route to the role's natural home — ignore any prior "from" state
            // so admins never accidentally land in /tech or /portal.
            nav(home, { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Error al iniciar sesión";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // If a session already exists when the user lands on /login, send them
    // directly to their natural home instead of showing the form.
    React.useEffect(() => {
        try {
            const raw = localStorage.getItem("trulyn_user");
            if (raw) {
                const u = JSON.parse(raw);
                const home = u.role === "technician" ? "/tech" : u.role === "client" ? "/portal" : u.role === "auditor" ? "/audit" : firstAllowedAdminPath(u);
                nav(home, { replace: true });
            }
        } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fillDemo = (a) => {
        // Clear any previous session so demo login is always clean
        try { localStorage.removeItem("trulyn_token"); localStorage.removeItem("trulyn_user"); } catch {}
        setEmail(a.email);
        setPassword(a.password);
    };

    const seed = async () => {
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed`, { method: "POST" });
            const data = await r.json();
            toast.success(`Datos demo listos${data.created?.length ? ": " + data.created.join(", ") : ""}`);
        } catch {
            toast.error("No se pudo inicializar demo");
        }
    };

    const resetToday = async () => {
        if (!window.confirm("¿Reiniciar TODOS los servicios de hoy del técnico demo a 'pendiente'? Esto borrará los reportes generados hoy.")) return;
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed/reset-today`, { method: "POST" });
            const data = await r.json();
            toast.success(`${data.services_reset || 0} servicios reiniciados, ${data.reports_deleted || 0} reportes borrados`);
        } catch {
            toast.error("No se pudieron reiniciar los servicios");
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row">
            {/* Left brand panel */}
            <div className="relative overflow-hidden bg-black text-white lg:w-1/2 px-8 py-10 lg:py-16 noise-overlay">
                <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full"
                     style={{ background: "radial-gradient(circle, rgba(255,214,43,0.22), transparent 65%)" }} />
                <div className="absolute -bottom-32 -left-32 w-[32rem] h-[32rem] rounded-full"
                     style={{ background: "radial-gradient(circle, rgba(255,0,0,0.18), transparent 65%)" }} />
                <div className="relative max-w-md mx-auto lg:mx-0">
                    <BrandLogo size={140} light />
                    <h1 className="mt-10 text-4xl sm:text-5xl font-semibold leading-[1.05]" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        Gestión Total de <span className="text-[#FF0000]">Servicios MIP</span>
                    </h1>
                    <p className="mt-4 text-white/75 text-xl leading-relaxed italic">
                        “IA + trazabilidad total = resultados visibles”
                    </p>
                </div>
            </div>

            {/* Right login */}
            <div className="flex-1 flex items-center justify-center px-6 py-10 bg-background">
                <div className="w-full max-w-md">
                    <Card className="border-border/70 shadow-lg">
                        <CardHeader className="pb-4">
                            <div className="lg:hidden mb-4">
                                <BrandLogo size={52} />
                            </div>
                            <CardTitle className="text-2xl">Iniciar sesión</CardTitle>
                            <CardDescription>Accede a tu cuenta TRULY IA360</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                                <div>
                                    <Label htmlFor="email">Usuario o correo</Label>
                                    <Input
                                        id="email"
                                        type="text"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="usuario o correo electrónico"
                                        autoComplete="username"
                                        className="mt-1.5 h-11"
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="password">Contraseña</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        className="mt-1.5 h-11"
                                        data-testid="login-password-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full h-11 text-base bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-[hsl(var(--accent-foreground))] font-semibold"
                                    disabled={loading}
                                    data-testid="login-submit-button"
                                >
                                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ingresando...</> : "Entrar"}
                                </Button>
                            </form>

                            <div className="mt-6">
                                <button
                                    type="button"
                                    onClick={seed}
                                    className="mt-3 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                                    data-testid="seed-demo-button"
                                >
                                    Inicializar datos demo
                                </button>
                                <span className="mx-2 text-xs text-muted-foreground">·</span>
                                <button
                                    type="button"
                                    onClick={resetToday}
                                    className="mt-3 text-xs text-[#FF0000] hover:underline underline-offset-2 font-semibold"
                                    data-testid="reset-today-button"
                                    title="Resetea todos los servicios de hoy del técnico demo a 'pendiente'"
                                >
                                    Reiniciar servicios demo de hoy
                                </button>
                            </div>
                        </CardContent>
                    </Card>
                    <p className="mt-6 text-center text-xs text-muted-foreground">
                        TRULYN, S.A. de C.V. • Truly Nolen El Salvador
                    </p>
                </div>
            </div>
        </div>
    );
}
